import {addDays, startOfDay} from 'date-fns';
import {FormField, FormControl} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {useEffect} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {
  useGetTireOrderDestinationBranchQuery,
  useGetTireSetDestinationBranchQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, parseDate, getApiDateString, Nullish} from 'shared/web';

import {useTireSetActions} from '../hooks/useTireSetActions';
import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';

interface RequestTireSetFieldsProps extends TestIdProps {
  control: FormControl<TireSetDetailFormDefinition>;
  formApi?: UseFormReturn<TireSetDetailFormDefinition>;
  tireOrderId?: string;
  tireSetId?: string;
  tireSetState?: string | Nullish;
  isEdit?: boolean;
  isDraft?: boolean;
  homeBranchId: string | Nullish;
}

export function RequestTireSetFields(props: RequestTireSetFieldsProps) {
  const tireSetActions = useTireSetActions({
    tireOrderId: props.tireOrderId ?? '',
    tireSetId: props.tireSetId ?? '',
    control: props.control,
    homeBranchId: props.homeBranchId,
  });

  const requestActions = props.isDraft
    ? {
        availableActions: [],
        canRequest: isNotNil(props.tireSetState),
        canModifyRequest: isNotNil(props.tireSetState),
        canCancelRequest: isNotNil(props.tireSetState),
      }
    : {
        availableActions: tireSetActions.availableActions,
        canRequest: tireSetActions.availableActions.some(
          (action: {key: string}) => action.key === 'request'
        ),
        canModifyRequest: tireSetActions.availableActions.some(
          (action: {key: string}) => action.key === 'modifyRequest'
        ),
        canCancelRequest: tireSetActions.availableActions.some(
          (action: {key: string}) => action.key === 'cancelRequest'
        ),
      };

  const {data: tireSetDestinationBranches, isLoading: isTireSetDestinationBranchesLoading} =
    useGetTireSetDestinationBranchQuery(
      {
        orderId: props.tireOrderId || '',
        setId: props.tireSetId || '',
      },
      {
        skip: !props.tireOrderId || !props.tireSetId,
      }
    );

  const {data: tireOrderDestinationBranches, isLoading: isTireOrderDestinationBranchesLoading} =
    useGetTireOrderDestinationBranchQuery(
      {
        orderId: props.tireOrderId || '',
      },
      {
        skip: !props.tireOrderId || !!props.tireSetId,
      }
    );

  const destinationBranches = props.tireSetId
    ? tireSetDestinationBranches
    : tireOrderDestinationBranches;

  const isDestinationBranchesLoading = props.tireSetId
    ? isTireSetDestinationBranchesLoading
    : isTireOrderDestinationBranchesLoading;

  const isEnabled = useWatch({
    control: props.control,
    name: 'tireSetRequestEnabled',
    defaultValue: false,
  });

  const selectedDestinationBranchId = useWatch({
    control: props.control,
    name: 'destinationBranchId',
  });

  // Pre-fill destinationBranchId
  useEffect(() => {
    if (
      isEnabled &&
      destinationBranches &&
      destinationBranches.length > 0 &&
      !selectedDestinationBranchId &&
      props.formApi
    ) {
      const defaultBranch = destinationBranches.find((branch) => branch.isDefault);
      if (defaultBranch) {
        props.formApi.setValue('destinationBranchId', defaultBranch.branchId);
      }
    }
  }, [isEnabled, destinationBranches, selectedDestinationBranchId, props.formApi]);

  // Pre-fill neededAtDate and calculate disabled dates based on selected branch
  useEffect(() => {
    const selectedBranch = destinationBranches?.find(
      (branch) => branch.branchId === selectedDestinationBranchId
    );

    if (selectedBranch && isEnabled && props.formApi) {
      const currentNeededAtDate = props.formApi.getValues('neededAtDate');
      const minimumDate = addDays(startOfDay(new Date()), selectedBranch.transitTimeDays);

      // Pre-fill neededAtDate if not already set
      if (!currentNeededAtDate) {
        props.formApi.setValue('neededAtDate', getApiDateString(minimumDate));
      } else {
        // Check if current date is now disabled (before minimum allowed date)
        const currentDateStart = startOfDay(parseDate(currentNeededAtDate));
        const minimumDateStart = startOfDay(minimumDate);

        if (currentDateStart < minimumDateStart) {
          // Current date is disabled, set to minimum allowed date
          props.formApi.setValue('neededAtDate', getApiDateString(minimumDate));
        }
      }
    }
  }, [selectedDestinationBranchId, destinationBranches, isEnabled, props.formApi]);

  // Pre-fill neededAtTime
  useEffect(() => {
    if (isEnabled && props.formApi) {
      const currentNeededAtTime = props.formApi.getValues('neededAtTime');

      // Pre-fill neededAtTime if not already set
      if (!currentNeededAtTime) {
        props.formApi.setValue('neededAtTime', '07:00');
      }
    }
  }, [isEnabled, props.formApi]);

  const destinationBranchesOptions = (destinationBranches ?? []).map((branch) => ({
    label: branch.branchName,
    value: branch.branchId,
  }));

  // Calculate disabled dates for DatePicker
  const getDisabledDates = () => {
    const selectedBranch = destinationBranches?.find(
      (branch) => branch.branchId === selectedDestinationBranchId
    );

    if (!selectedBranch) {
      return [];
    }

    const today = startOfDay(new Date());

    // Generate array of disabled dates from today to transitTimeDays (exclusive)
    const disabledDates = [];
    for (let i = 0; i < selectedBranch.transitTimeDays; i++) {
      disabledDates.push(addDays(today, i));
    }

    return disabledDates;
  };

  const canRequest = requestActions?.canRequest ?? false;
  const canModifyRequest = requestActions?.canModifyRequest ?? false;
  const canCancelRequest = requestActions?.canCancelRequest ?? false;
  const canEditFields = canRequest || canModifyRequest;
  const canToggle = canRequest || canModifyRequest || canCancelRequest;

  return (
    <VStack spacing={4}>
      <Box width="fit-content">
        <FormField
          control={props.control}
          label={i18n.t('entity.tireSet.labels.request')}
          name="tireSetRequestEnabled"
          type="switch"
          isDisabled={!canToggle}
          data-testid={suffixTestId('tireSetRequest', props)}
        />
      </Box>
      {isEnabled && (
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              label={i18n.t('entity.tiresInventory.labels.branch')}
              name="destinationBranchId"
              type="choice"
              options={destinationBranchesOptions}
              isLoading={isDestinationBranchesLoading}
              isRequired
              isDisabled={!canEditFields}
              isNotClearable
              data-testid={suffixTestId('requestedBranchId', props)}
            />
          </Box>
          <Box flex={1}>
            <HStack spacing={4} align="flex-end">
              <FormField
                control={props.control}
                label={i18n.t('entity.tiresInventory.labels.neededAt')}
                isRequired
                name="neededAtDate"
                type="apiDate"
                isDisabled={!canEditFields}
                disabledDates={getDisabledDates()}
                minDate={startOfDay(new Date())}
                data-testid={suffixTestId('neededAtDate', props)}
              />
              <FormField
                control={props.control}
                name="neededAtTime"
                isRequired
                type="time"
                isDisabled={!canEditFields}
                data-testid={suffixTestId('neededAtTime', props)}
              />
            </HStack>
          </Box>
          <Box flex={2}>
            <FormField
              control={props.control}
              label={i18n.t('general.labels.note')}
              name="requestNote"
              type="textarea"
              isDisabled={!canEditFields}
              data-testid={suffixTestId('note', props)}
            />
          </Box>
        </HStack>
      )}
    </VStack>
  );
}
